import { useState, useCallback, useEffect } from "react";
import {
  Box, TextField, Paper, Select, MenuItem, FormControl, InputLabel, Button, Popover,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateCalendar, LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminFilterPanel from "../../components/admin/AdminFilterPanel";
import { listAuditLog } from "../../services/adminService";
import { ADMIN_PAPER_SX, ADMIN_GRID_SX } from "../../components/admin/adminGridStyles";

const formatBr  = (d) => d ? dayjs(d).format("DD/MM/YYYY") : "";
const toIso     = (d) => d ? dayjs(d).format("YYYY-MM-DD") : "";
const isSameDay = (a, b) => a && b && dayjs(a).isSame(dayjs(b), "day");
const isBetween = (day, s, e) =>
  s && e && dayjs(day).isAfter(dayjs(s), "day") && dayjs(day).isBefore(dayjs(e), "day");

function RangeDay({ day, startDate, endDate, ...other }) {
  const isStart   = isSameDay(day, startDate);
  const isEnd     = isSameDay(day, endDate);
  const isInRange = isBetween(day, startDate, endDate);
  return (
    <PickersDay {...other} day={day} selected={false} sx={{
      borderRadius: isStart || isEnd ? "50%" : 0,
      backgroundColor: isStart || isEnd ? "primary.main" : isInRange ? "primary.light" : undefined,
      color: isStart || isEnd ? "primary.contrastText" : undefined,
    }} />
  );
}

const ACOES = [
  "", "LOGIN", "CADASTRO_USUARIO", "EDITAR_USUARIO", "DELETAR_USUARIO",
  "CADASTRO_EMPRESA", "EDITAR_EMPRESA", "DELETAR_EMPRESA",
  "CADASTRO_CERTIFICADO", "DELETAR_CERTIFICADO",
  "UPLOAD_DOCUMENTOS", "DELETAR_NOTA", "DELETAR_CTE",
];
const ACAO_LABELS = {
  "": "Todas",
  LOGIN: "Login",
  CADASTRO_USUARIO: "Cadastro de usuário",
  EDITAR_USUARIO: "Edição de usuário",
  DELETAR_USUARIO: "Exclusão de usuário",
  CADASTRO_EMPRESA: "Cadastro de empresa",
  EDITAR_EMPRESA: "Edição de empresa",
  DELETAR_EMPRESA: "Exclusão de empresa",
  CADASTRO_CERTIFICADO: "Cadastro de certificado",
  DELETAR_CERTIFICADO: "Exclusão de certificado",
  UPLOAD_DOCUMENTOS: "Upload de documentos",
  DELETAR_NOTA: "Exclusão de NF-e",
  DELETAR_CTE: "Exclusão de CT-e",
};

const EMPTY_FILTERS = { acao: "", loginUsuario: "", dateRange: "", dateFrom: "", dateTo: "" };

const columns = [
  {
    field: "created_at", headerName: "Data/Hora", width: 170,
    valueFormatter: (v) => { try { return v ? new Date(v).toLocaleString("pt-BR") : "—"; } catch { return v; } },
  },
  { field: "acao", headerName: "Ação", width: 200, valueFormatter: (v) => ACAO_LABELS[v] ?? v },
  { field: "login_usuario", headerName: "Usuário", flex: 1 },
  { field: "usuario_afetado", headerName: "Usuário Afetado", flex: 1, valueFormatter: (v) => v || "—" },
  { field: "chave_afetada", headerName: "Chave Afetada", flex: 1.5, valueFormatter: (v) => v || "—" },
];

export default function AuditLogPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [rangeStart, setRangeStart]         = useState(null);
  const [rangeEnd, setRangeEnd]             = useState(null);
  const [selectingEnd, setSelectingEnd]     = useState(false);

  const load = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.acao)         params.acao         = f.acao;
      if (f.loginUsuario) params.loginUsuario = f.loginUsuario.trim();
      if (f.dateFrom)     params.dateFrom      = f.dateFrom;
      if (f.dateTo)       params.dateTo        = f.dateTo;
      const data = await listAuditLog(params);
      setRows(Array.isArray(data) ? data : (data?.items ?? []));
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(EMPTY_FILTERS); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => load(filters);

  const sf = (field) => ({
    value: filters[field],
    onChange: (e) => setFilters((p) => ({ ...p, [field]: e.target.value })),
  });

  const handleCalendarChange = (newDate) => {
    if (!selectingEnd || !rangeStart) {
      setRangeStart(newDate); setRangeEnd(newDate); setSelectingEnd(true);
      setFilters((p) => ({ ...p, dateRange: formatBr(newDate), dateFrom: toIso(newDate), dateTo: toIso(newDate) }));
      return;
    }
    let start = rangeStart, end = newDate;
    if (dayjs(end).isBefore(dayjs(start), "day")) [start, end] = [end, start];
    setRangeStart(start); setRangeEnd(end); setSelectingEnd(false);
    const same = dayjs(start).isSame(dayjs(end), "day");
    setFilters((p) => ({
      ...p,
      dateRange: same ? formatBr(start) : `${formatBr(start)} - ${formatBr(end)}`,
      dateFrom:  toIso(start),
      dateTo:    toIso(end),
    }));
    setCalendarAnchor(null);
  };

  const clearDate = () => {
    setRangeStart(null); setRangeEnd(null); setSelectingEnd(false);
    setFilters((p) => ({ ...p, dateRange: "", dateFrom: "", dateTo: "" }));
    setCalendarAnchor(null);
  };

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <AdminPageHeader title="Log de Atividades" />

      <AdminFilterPanel>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel shrink>Ação</InputLabel>
          <Select {...sf("acao")} label="Ação" notched displayEmpty>
            {ACOES.map((a) => (
              <MenuItem key={a} value={a}>{ACAO_LABELS[a] ?? a}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField label="Usuário" size="small" {...sf("loginUsuario")}
          InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />

        <TextField
          label="Data" value={filters.dateRange || ""} size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
          onClick={(e) => setCalendarAnchor(e.currentTarget)}
          sx={{ width: 170 }}
        />
        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={() => setCalendarAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 1, pb: 0 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={rangeEnd || rangeStart || dayjs()}
                onChange={handleCalendarChange}
                slots={{ day: RangeDay }}
                slotProps={{ day: { startDate: rangeStart, endDate: rangeEnd } }}
              />
            </LocalizationProvider>
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button size="small" onClick={clearDate} sx={{ textTransform: "none" }}>Limpar</Button>
            </Box>
          </Box>
        </Popover>

        <Button variant="contained" onClick={handleFilter} disabled={loading} sx={{ textTransform: "none" }}>
          Buscar
        </Button>
      </AdminFilterPanel>

      <Paper sx={ADMIN_PAPER_SX}>
        <DataGrid
          rows={rows} columns={columns} getRowId={(r) => r.id}
          loading={loading} autoHeight disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={ADMIN_GRID_SX}
        />
      </Paper>
    </Box>
  );
}
